import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Check, Pencil, Linkedin, Instagram, Twitter, Mail, Monitor, Presentation } from "lucide-react";

const PLATFORM_META: Record<string, { label: string; icon: typeof Linkedin; accent: string }> = {
  linkedin: { label: "LinkedIn", icon: Linkedin, accent: "text-[#0A66C2]" },
  linkedin_post: { label: "LinkedIn", icon: Linkedin, accent: "text-[#0A66C2]" },
  instagram: { label: "Instagram", icon: Instagram, accent: "text-[#E1306C]" },
  instagram_caption: { label: "Instagram", icon: Instagram, accent: "text-[#E1306C]" },
  x: { label: "X", icon: Twitter, accent: "text-foreground" },
  x_single: { label: "X", icon: Twitter, accent: "text-foreground" },
  x_thread: { label: "X Thread", icon: Twitter, accent: "text-foreground" },
  email: { label: "Email", icon: Mail, accent: "text-brand-600" },
  email_template: { label: "Email", icon: Mail, accent: "text-brand-600" },
  website: { label: "Website", icon: Monitor, accent: "text-success" },
  presentation: { label: "Slides", icon: Presentation, accent: "text-warning" },
  slide_bullets: { label: "Slides", icon: Presentation, accent: "text-warning" },
  one_liner: { label: "Quote", icon: Monitor, accent: "text-muted-foreground" },
  case_study: { label: "Case Study", icon: Monitor, accent: "text-brand-700" },
};

interface OutputCardProps {
  platform: string;
  content: string;
  onEdit?: (platform: string, content: string) => void;
}

const OutputCard = ({ platform, content, onEdit }: OutputCardProps) => {
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(content);

  const meta = PLATFORM_META[platform] || PLATFORM_META["website"];
  const Icon = meta.icon;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success(`${meta.label} content copied!`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveEdit = () => {
    setIsEditing(false);
    onEdit?.(platform, editContent);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl ink-border bg-card overflow-hidden"
    >
      {/* Header — specimen style */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${meta.accent}`} />
          <span className="specimen-label !text-foreground">{meta.label}</span>
        </div>
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setIsEditing(!isEditing)}>
            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleCopy}>
            {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
          </Button>
        </div>
      </div>

      <div className="p-4">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              className="w-full p-3 rounded-lg border border-border bg-background text-sm resize-none min-h-[100px] focus:ring-2 focus:ring-brand-500 outline-none"
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => { setIsEditing(false); setEditContent(content); }}>Cancel</Button>
              <Button size="sm" onClick={handleSaveEdit} className="gradient-brand text-white">Save</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{content}</p>
        )}
      </div>
    </motion.div>
  );
};

export default OutputCard;
